import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    BASEMENT_WALL_EXTRA_INSULATION_LAYER,
    getLayerEmbCarbonFieldKey,
    getLayerEmbodiedCarbonDisplay,
    getLayerMaterialAltFieldKey,
    INSULATION_LAYER_1,
    INSULATION_LAYER_2,
    INTERIOR,
    resolveInteriorExteriorMaterialRow,
    SHEATHING,
    EXTERIOR,
} from "utils/enclosure/embodiedCarbon";
import { parseEmbodiedNum, useEmbodiedFieldId, useExpandableGradeCellHandlers } from "./embodiedFormUtils";

import GradeDisplay from "./GradeDisplay";
import PercentageDisplay from "./PercentageDisplay";

import classes from "./style.module.scss";

const MAP_BY_LAYER = {
    insulationLayer1: INSULATION_LAYER_1,
    insulationLayer2: INSULATION_LAYER_2,
    interior: INTERIOR,
    interiorFinish: INTERIOR,
    insulationFramingLayer: INSULATION_LAYER_1,
    extraInsulationLayer: BASEMENT_WALL_EXTRA_INSULATION_LAYER,
    sheathing: SHEATHING,
    exterior: EXTERIOR,
};

export const USER_DEFINED_LABEL = "User Defined";

/** Keeps letters, numbers, whitespace, and citation-friendly punctuation; strips e.g. !@#$%^*+=[]{}| */
const SOURCE_INPUT_STRIP = /[^\p{L}\p{N}\s\-,.:;'"()/&]/gu;

export const sanitizeSourceInput = (raw) => {
    if (raw == null || raw === "") return "";
    return String(raw).replace(SOURCE_INPUT_STRIP, "");
};

/**
 * standard code row: select field + embodied carbon grade with optional raw EPD editor.
 */
const StandardEmbodiedLayerBlock = ({
    layerType,
    layers = {},
    change,
    layersFieldPrefix,
    children,
    componentType = "wall",
    editorDisabled = false,
    withBorder = false,
    borderHeight = "100%",
    customGap = "0.75rem",
    customPadding = "0 7px",
    rowStyle = {},
    barMetric = "percentage",
}) => {
    const [expanded, setExpanded] = useState(false);
    const [draftEmissions, setDraftEmissions] = useState("");
    const [draftStorage, setDraftStorage] = useState("");
    const [draftSource, setDraftSource] = useState("");
    const sourceInputId = useEmbodiedFieldId(`volta-emb-carbon-source-${layerType}`);
    const expandCellHandlers = useExpandableGradeCellHandlers(setExpanded);

    const canInteract = !editorDisabled;

    useEffect(() => {
        if (!canInteract) setExpanded(false);
    }, [canInteract]);

    const embKey = getLayerEmbCarbonFieldKey(layerType);
    const embPath = `${layersFieldPrefix}.${embKey}`;
    const embC = layers[embKey];
    const layerSelect = layers[layerType];
    const codeStr = (layerSelect?.value ?? "0").toString();
    const materialAltField =
        layerType === "interior" || layerType === "exterior" || layerType === "interiorFinish"
            ? getLayerMaterialAltFieldKey(layerType)
            : null;
    const materialAltPath = materialAltField ? `${layersFieldPrefix}.${materialAltField}` : null;
    const rawMapRow = useMemo(() => MAP_BY_LAYER[layerType]?.[codeStr] || {}, [layerType, codeStr]);
    const materialAltKeyFromLayers = materialAltField ? layers[materialAltField] : undefined;
    const defaultRow = useMemo(
        () =>
            materialAltField
                ? resolveInteriorExteriorMaterialRow(rawMapRow, materialAltKeyFromLayers)
                : rawMapRow,
        [materialAltField, rawMapRow, materialAltKeyFromLayers]
    );

    const display = useMemo(
        () => getLayerEmbodiedCarbonDisplay({ layerType, layers, componentType }),
        [layerType, layers, componentType]
    );

    const defaultSourceText = defaultRow.source || "";

    useEffect(() => {
        if (!expanded || editorDisabled) return;

        if (embC && embC.emissions != null) {
            setDraftEmissions(String(embC.emissions));
            setDraftStorage(embC.storage != null ? String(embC.storage) : "");
            setDraftSource(
                sanitizeSourceInput(embC.source != null && embC.source !== "" ? embC.source : USER_DEFINED_LABEL)
            );
        } else {
            setDraftEmissions(defaultRow.emissions != null ? String(defaultRow.emissions) : "");
            setDraftStorage(defaultRow.storage != null ? String(defaultRow.storage) : "");
            setDraftSource("");
        }
    }, [expanded, editorDisabled, embC, defaultRow, layerType]);

    useEffect(() => {
        if (!materialAltPath || rawMapRow?.alt) return;
        if (materialAltKeyFromLayers != null && materialAltKeyFromLayers !== "") {
            change(materialAltPath, "");
        }
    }, [change, codeStr, materialAltPath, materialAltKeyFromLayers, rawMapRow?.alt]);

    const applyEmbC = useCallback(() => {
        const e = parseEmbodiedNum(draftEmissions);
        const s = parseEmbodiedNum(draftStorage);
        if (e == null) return;

        const trimmed = sanitizeSourceInput(draftSource).trim();
        const nextSource = trimmed === "" ? USER_DEFINED_LABEL : trimmed;

        change(embPath, {
            emissions: e,
            storage: s ?? 0,
            source: nextSource,
            layerCodeId: layerSelect?.id,
            layerCodeValue: layerSelect?.value,
            ...(materialAltField != null ? { materialAltKey: String(materialAltKeyFromLayers ?? "") } : {}),
        });
    }, [
        change,
        embPath,
        draftEmissions,
        draftStorage,
        draftSource,
        layerSelect,
        materialAltField,
        materialAltKeyFromLayers,
    ]);

    const resetEmbC = useCallback(() => {
        change(embPath, null);
        if (materialAltPath && materialAltKeyFromLayers != null && materialAltKeyFromLayers !== "") {
            change(materialAltPath, "");
        }
        setExpanded(false);
    }, [change, embPath, materialAltPath, materialAltKeyFromLayers]);

    const showGrade = barMetric === "grade";

    const gradeInner = (
        <GradeDisplay
            emissions={display.emissions}
            storage={display.storage}
            emissionGrade={display.emissionGrade}
            storageGrade={display.storageGrade}
            withBorder={withBorder}
            borderHeight={borderHeight}
            customGap={customGap}
            customPadding={customPadding}
        />
    );

    const percentageInner = (
        <PercentageDisplay
            emissions={display.emissions}
            storage={display.storage}
            withBorder={withBorder}
            borderHeight={borderHeight}
            customGap={customGap}
            customPadding={customPadding}
        />
    );

    return (
        <div className={classes.wallLayerRoot} style={rowStyle}>
            <div className={classes.wallLayerGrid}>
                <div className={classes.wallLayerFieldCell}>{children}</div>
                <div
                    className={`${classes.wallLayerGradeCell} ${display.hasMismatch ? classes.mismatch : ""} ${
                        canInteract ? classes.clickable : ""
                    }`}
                    {...(canInteract ? expandCellHandlers : {})}
                >
                    {showGrade ? gradeInner : percentageInner}
                </div>
            </div>
            {expanded && canInteract && (
                <div className={classes.editorPanel} onClick={(e) => e.stopPropagation()}>
                    {display.hasMismatch && (
                        <div className={classes.mismatchNotice}>
                            Data mismatch. EPD data entered for code value &apos;{display.mismatchCodeLabel ?? "—"}
                            &apos;.
                        </div>
                    )}
                    <div className={classes.editorRow}>
                        <div className={classes.editorRowInputs}>
                            <label className={classes.miniLabel}>
                                Emissions (kgCO₂/m²)
                                <input
                                    className={classes.miniInput}
                                    type="text"
                                    inputMode="decimal"
                                    value={draftEmissions}
                                    onChange={(ev) => setDraftEmissions(ev.target.value)}
                                />
                            </label>
                            <label className={classes.miniLabel}>
                                Storage (kgCO₂/m²)
                                <input
                                    className={classes.miniInput}
                                    type="text"
                                    inputMode="decimal"
                                    value={draftStorage}
                                    onChange={(ev) => setDraftStorage(ev.target.value)}
                                />
                            </label>
                            {rawMapRow?.alt && materialAltPath && (
                                <label className={classes.miniLabel}>
                                    Specification
                                    <select
                                        className={classes.woodProvinceSelect}
                                        value={String(materialAltKeyFromLayers ?? "")}
                                        onChange={(ev) => change(materialAltPath, ev.target.value)}
                                    >
                                        <option value="">{rawMapRow.defaultLabel ?? "Default"}</option>
                                        {Object.keys(rawMapRow.alt).map((k) => (
                                            <option key={k} value={k}>
                                                {k}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            <button
                                type="button"
                                className={classes.resetBtn}
                                title="Reset to defaults"
                                onClick={resetEmbC}
                            >
                                <span className={classes.resetBtnIcon} aria-hidden="true">
                                    ↺
                                </span>
                            </button>
                        </div>
                        <button type="button" className={classes.applyBtn} onClick={applyEmbC}>
                            Apply
                        </button>
                    </div>
                    <div className={classes.sourceRow}>
                        <label className={classes.sourceLabel} htmlFor={sourceInputId}>
                            Source
                        </label>
                        <input
                            className={`${classes.sourceInput} keeper-ignore`}
                            type="text"
                            name={`voltaEmbCarbonCitation-${layerType}`}
                            id={sourceInputId}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-bwignore="true"
                            data-form-type="other"
                            placeholder={embC ? USER_DEFINED_LABEL : defaultSourceText || "—"}
                            value={draftSource}
                            onChange={(ev) => setDraftSource(sanitizeSourceInput(ev.target.value))}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandardEmbodiedLayerBlock;
